import { useMemo } from 'react';

import { DefaultError, InfiniteData, useInfiniteQuery } from '@tanstack/react-query';

import { Dimensions, Metrics, Sources } from 'components/insights/constants';
import AnalyticsResponse from 'components/insights/query/AnalyticsResponse';
import { Analytics, retrieveAnalytics } from 'services/api/insights/analytics';
import { getReferralAnalyticsFilters, useReferralsInsightsStore } from 'stores/referralsInsightsStore';

type QueryResult = Awaited<ReturnType<typeof retrieveAnalytics>>;

export type ReferralAnalyticsQueryParams = {
  source: Sources;
  dimensions?: (Dimensions | string)[];
  metrics: (Metrics | string)[];
  sortBy?: string;
  rollups?: boolean;
  accessModes?: string[];
};

type Options = {
  queryKey?: string;
  enabled?: boolean;
  // Additional filters merged on top of the dashboard's filters. Used by the slideout to lock
  // results to a single entity without mutating shared dashboard state.
  extraFilters?: Record<string, unknown> | null;
};

const defaultOptions = {
  getNextPageParam: () => null,
  initialPageParam: null,
};

export default function useReferralAnalytics(
  queryParams: ReferralAnalyticsQueryParams,
  { queryKey = 'referralAnalytics', enabled = true, extraFilters = null }: Options = {}
) {
  const baseFilters = useReferralsInsightsStore((state) => getReferralAnalyticsFilters(state));
  const analyticsFilters = useMemo(
    () => (baseFilters ? { ...baseFilters, ...(extraFilters ?? {}) } : null),
    [baseFilters, extraFilters]
  );

  const query = useInfiniteQuery<QueryResult, DefaultError, InfiniteData<Analytics, Analytics>>({
    queryKey: [queryKey, queryParams, analyticsFilters],
    queryFn: async ({ pageParam }) =>
      retrieveAnalytics({ ...queryParams, ...(pageParam as ReferralAnalyticsQueryParams) }, analyticsFilters),
    enabled: enabled && !!analyticsFilters,
    ...defaultOptions,
  });

  const response = useMemo(() => new AnalyticsResponse(query.data, false), [query.data]);

  return {
    loading: query.isPending || query.isRefetching,
    query,
    data: response.data,
    rollups: response.rollups,
  };
}
